import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { Card, Button, Input, Modal } from '@components/common/UIComponents';
import {
  saveSteps,
  saveHeartPoints,
  getSteps,
  getHeartPoints,
  getStepsInRange,
  getHeartPointsInRange,
  getUserSettings
} from '@services/firestoreService';
import { Activity, Heart, Calendar, TrendingUp, ChevronLeft, ChevronRight, Award } from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths, subDays, subWeeks, subMonths, eachDayOfInterval } from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ActivityPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('week'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showStepsModal, setShowStepsModal] = useState(false);
  const [showHeartPointsModal, setShowHeartPointsModal] = useState(false);
  
  const [periodData, setPeriodData] = useState({
    steps: [],
    heartPoints: []
  });
  
  const [todayData, setTodayData] = useState({
    steps: 0,
    heartPoints: 0
  });
  
  const [goals, setGoals] = useState({
    stepsGoal: 8000,
    heartPointsGoal: 20
  });

  const [formData, setFormData] = useState({
    steps: '',
    heartPoints: ''
  });

  useEffect(() => {
    loadData();
  }, [user, period, currentDate]);

  const getPeriodRange = () => {
    if (period === 'day') {
      return {
        start: startOfDay(currentDate),
        end: endOfDay(currentDate)
      };
    } else if (period === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }), // Monday
        end: endOfWeek(currentDate, { weekStartsOn: 1 })
      };
    } else {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate)
      };
    }
  };

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Load settings
      const settings = await getUserSettings(user.uid);
      if (settings) {
        setGoals({
          stepsGoal: settings.stepsGoal || 8000,
          heartPointsGoal: settings.heartPointsGoal || 20
        });
      }

      // Load today's data
      const today = new Date();
      const [todaySteps, todayHeartPoints] = await Promise.all([
        getSteps(user.uid, today),
        getHeartPoints(user.uid, today)
      ]);

      setTodayData({
        steps: todaySteps?.steps || 0,
        heartPoints: todayHeartPoints?.points || 0
      });

      // Load period data
      const { start, end } = getPeriodRange();
      const [stepsData, heartPointsData] = await Promise.all([
        getStepsInRange(user.uid, start, end),
        getHeartPointsInRange(user.uid, start, end)
      ]);

      setPeriodData({
        steps: stepsData,
        heartPoints: heartPointsData
      });
    } catch (error) {
      console.error('Error loading activity data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (direction) => {
    if (period === 'day') {
      setCurrentDate(direction === 'prev' ? subDays(currentDate, 1) : addDays(currentDate, 1));
    } else if (period === 'week') {
      setCurrentDate(direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
    }
  };

  const handleSaveSteps = async () => {
    if (!formData.steps || formData.steps < 0) return;

    try {
      const today = new Date();
      await saveSteps(user.uid, today, parseInt(formData.steps));
      setShowStepsModal(false);
      setFormData({ ...formData, steps: '' });
      loadData();
    } catch (error) {
      console.error('Error saving steps:', error);
    }
  };

  const handleSaveHeartPoints = async () => {
    if (!formData.heartPoints || formData.heartPoints < 0) return;

    try {
      const today = new Date();
      await saveHeartPoints(user.uid, today, parseInt(formData.heartPoints));
      setShowHeartPointsModal(false);
      setFormData({ ...formData, heartPoints: '' });
      loadData();
    } catch (error) {
      console.error('Error saving heart points:', error);
    }
  };

  // Calculate statistics
  const calculateStats = (data, field) => {
    if (data.length === 0) return { total: 0, average: 0, max: 0, daysAchieved: 0 };
    
    const values = data.map(item => item[field] || 0);
    const total = values.reduce((sum, val) => sum + val, 0);
    const average = Math.round(total / values.length);
    const max = Math.max(...values);
    const goal = field === 'steps' ? goals.stepsGoal : goals.heartPointsGoal;
    const daysAchieved = values.filter(val => val >= goal).length;
    
    return { total, average, max, daysAchieved };
  };

  const stepsStats = calculateStats(periodData.steps, 'steps');
  const heartPointsStats = calculateStats(periodData.heartPoints, 'points');

  // Prepare chart data
  const { start, end } = getPeriodRange();
  const allDays = eachDayOfInterval({ start, end });
  
  const getChartLabels = () => {
    if (period === 'day') {
      return ['Today'];
    } else if (period === 'week') {
      return allDays.map(day => format(day, 'EEE')); // Mon, Tue, Wed...
    } else {
      // For month, show every few days
      return allDays.filter((_, index) => index % Math.ceil(allDays.length / 10) === 0)
                    .map(day => format(day, 'MMM d'));
    }
  };

  const getChartData = (dataArray, field) => {
    if (period === 'day') {
      const todayValue = dataArray.find(item => 
        format(new Date(item.date), 'yyyy-MM-dd') === format(currentDate, 'yyyy-MM-dd')
      );
      return [todayValue?.[field] || 0];
    }
    
    return allDays.map(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const entry = dataArray.find(item => 
        format(new Date(item.date), 'yyyy-MM-dd') === dateStr
      );
      return entry?.[field] || 0;
    });
  };

  const stepsChartData = {
    labels: getChartLabels(),
    datasets: [
      {
        label: 'Steps',
        data: getChartData(periodData.steps, 'steps'),
        backgroundColor: 'rgba(14, 165, 233, 0.8)',
        borderColor: 'rgb(14, 165, 233)',
        borderWidth: 1
      }
    ]
  };

  const heartPointsChartData = {
    labels: getChartLabels(),
    datasets: [
      {
        label: 'Heart Points',
        data: getChartData(periodData.heartPoints, 'points'),
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderColor: 'rgb(239, 68, 68)',
        borderWidth: 1
      }
    ]
  };

  const getPeriodLabel = () => {
    if (period === 'day') {
      return format(currentDate, 'MMMM dd, yyyy');
    } else if (period === 'week') {
      const { start, end } = getPeriodRange();
      return `${format(start, 'MMM dd')} - ${format(end, 'MMM dd, yyyy')}`;
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          afterLabel: (context) => {
            const goal = context.dataset.label === 'Steps' ? goals.stepsGoal : goals.heartPointsGoal;
            return `Goal: ${goal}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => value.toLocaleString()
        }
      }
    }
  };

  const stepsProgress = (todayData.steps / goals.stepsGoal) * 100;
  const heartPointsProgress = (todayData.heartPoints / goals.heartPointsGoal) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Activity Tracking</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Monitor your steps and heart points
        </p>
      </div>

      {/* Period Selector */}
      <Card>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Period Tabs */}
          <div className="flex gap-2">
            <Button
              variant={period === 'day' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod('day')}
            >
              Daily
            </Button>
            <Button
              variant={period === 'week' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod('week')}
            >
              Weekly
            </Button>
            <Button
              variant={period === 'month' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod('month')}
            >
              Monthly
            </Button>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => handleNavigate('prev')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[200px] text-center">
              {getPeriodLabel()}
            </span>
            <button
              onClick={() => handleNavigate('next')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              disabled={format(currentDate, 'yyyy-MM-dd') >= format(new Date(), 'yyyy-MM-dd')}
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Quick Log Buttons */}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setShowStepsModal(true)}>
              Log Steps
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowHeartPointsModal(true)}>
              Log Points
            </Button>
          </div>
        </div>
      </Card>

      {/* Today's Progress (Always visible) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <Activity className="text-primary-600 dark:text-primary-400" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Today's Steps</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {format(new Date(), 'MMM dd, yyyy')}
              </p>
            </div>
          </div>
          
          <div className="mb-4">
            <div className="flex items-end justify-between mb-2">
              <span className="text-4xl font-bold text-gray-900 dark:text-gray-100">
                {todayData.steps.toLocaleString()}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                / {goals.stepsGoal.toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div
                className="bg-primary-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(stepsProgress, 100)}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {stepsProgress >= 100 ? '🎉 Goal achieved!' : `${Math.round(stepsProgress)}% of goal`}
            </p>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
              <Heart className="text-red-600 dark:text-red-400" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Today's Heart Points</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {format(new Date(), 'MMM dd, yyyy')}
              </p>
            </div>
          </div>
          
          <div className="mb-4">
            <div className="flex items-end justify-between mb-2">
              <span className="text-4xl font-bold text-gray-900 dark:text-gray-100">
                {todayData.heartPoints}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                / {goals.heartPointsGoal}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div
                className="bg-red-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(heartPointsProgress, 100)}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {heartPointsProgress >= 100 ? '🎉 Goal achieved!' : `${Math.round(heartPointsProgress)}% of goal`}
            </p>
          </div>
        </Card>
      </div>

      {/* Period Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {stepsStats.total.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Steps</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {stepsStats.average.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Avg Steps/Day</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {stepsStats.max.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Best Day</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-2xl font-bold text-primary-600 dark:text-primary-400">
              <Award size={24} />
              <span>{stepsStats.daysAchieved}</span>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Days Met Goal</div>
          </div>
        </Card>
      </div>

      {/* Steps Chart */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Steps Overview
        </h3>
        <div className="h-64">
          <Bar data={stepsChartData} options={chartOptions} />
        </div>
      </Card>

      {/* Heart Points Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {heartPointsStats.total}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Points</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {heartPointsStats.average}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Avg Points/Day</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {heartPointsStats.max}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Best Day</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-2xl font-bold text-red-600 dark:text-red-400">
              <Award size={24} />
              <span>{heartPointsStats.daysAchieved}</span>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Days Met Goal</div>
          </div>
        </Card>
      </div>

      {/* Heart Points Chart */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Heart Points Overview
        </h3>
        <div className="h-64">
          <Bar data={heartPointsChartData} options={chartOptions} />
        </div>
      </Card>

      {/* Steps Modal */}
      <Modal
        isOpen={showStepsModal}
        onClose={() => setShowStepsModal(false)}
        title="Log Steps"
      >
        <div className="space-y-4">
          <Input
            type="number"
            label="Number of Steps"
            placeholder="8000"
            value={formData.steps}
            onChange={(e) => setFormData({ ...formData, steps: e.target.value })}
            fullWidth
          />
          <div className="flex gap-3">
            <Button onClick={handleSaveSteps} fullWidth>
              Save
            </Button>
            <Button variant="secondary" onClick={() => setShowStepsModal(false)} fullWidth>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Heart Points Modal */}
      <Modal
        isOpen={showHeartPointsModal}
        onClose={() => setShowHeartPointsModal(false)}
        title="Log Heart Points"
      >
        <div className="space-y-4">
          <Input
            type="number"
            label="Heart Points"
            placeholder="20"
            value={formData.heartPoints}
            onChange={(e) => setFormData({ ...formData, heartPoints: e.target.value })}
            fullWidth
          />
          <div className="flex gap-3">
            <Button onClick={handleSaveHeartPoints} fullWidth>
              Save
            </Button>
            <Button variant="secondary" onClick={() => setShowHeartPointsModal(false)} fullWidth>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ActivityPage;
